<template>
  <div class="ai-check-container">
    <!-- Header Banner -->
    <div class="ai-header-banner">
      <div class="banner-overlay"></div>
      <div class="banner-content">
        <div class="banner-left">
          <div class="ai-icon-pulse">
            <i class="el-icon-setting"></i>
          </div>
          <div>
            <h1 class="banner-title">QUẢN LÝ CẤU HÌNH MERCHANT</h1>
            <p class="banner-subtitle">Quản lý từ khóa giao dịch, Logo, thông tin kết nối SePay và danh sách IP được chấp nhận.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Initial loading -->
    <div v-if="isInitialLoading" class="glass-card loading-container flex flex-column justify-center align-center">
      <i class="el-icon-loading" style="font-size: 40px; color: #3b82f6; margin-bottom: 12px;"></i>
      <p style="color: #64748b; font-weight: 600;">Đang tải thông tin cấu hình Merchant...</p>
    </div>

    <!-- Main Content -->
    <div v-else class="glass-card panel-container">
      <el-tabs v-model="activeTab" type="card" class="sk-tabs">
        
        <!-- Tab 1: Whitelist keywords -->
        <el-tab-pane name="keywords">
          <template slot="label">
            <i class="el-icon-collection-tag" /> Từ khóa Giao dịch
          </template>
          <div class="tab-inner-content">
            <!-- Rules description -->
            <div class="hint-panel blue-hint mb-4">
              <div class="hint-title">
                <i class="el-icon-info"></i> Cơ chế lọc giao dịch tự động
              </div>
              <ul class="hint-list">
                <li>Hệ thống sử dụng danh sách từ khóa bên dưới để lọc giao dịch trước khi kích hoạt gửi webhook.</li>
                <li>Chỉ các giao dịch có nội dung (transaction_content) chứa ít nhất một từ khóa mới được gửi webhook.</li>
                <li>Hệ thống <strong>không phân biệt chữ hoa, chữ thường</strong> khi so khớp.</li>
                <li>Nếu danh sách từ khóa rỗng, tất cả các giao dịch phát sinh sẽ tự động được gửi webhook.</li>
              </ul>
              <div class="example-box mt-2">
                <span class="ex-label">Ví dụ cấu hình:</span>
                <span class="ex-text">Từ khóa <code>["thanh toan", "payment"]</code> sẽ chấp nhận <code>"Chuyen khoan thanh toan don hang"</code> và từ chối <code>"Chuyen khoan cho ban"</code>.</span>
              </div>
            </div>

            <!-- Current keywords list -->
            <div class="keywords-list-section">
              <h3 class="section-title">Danh sách từ khóa Whitelist</h3>
              
              <div v-if="config.transaction_whitelist_remark_keywords.length === 0" class="empty-state-card mb-4">
                <i class="el-icon-document-delete"></i>
                <p class="empty-text">Chưa có từ khóa nào được thiết lập. Hệ thống sẽ cho phép gửi webhook cho tất cả giao dịch.</p>
              </div>

              <div v-else class="keywords-grid mb-4">
                <el-tag
                  v-for="(keyword, index) in config.transaction_whitelist_remark_keywords"
                  :key="index"
                  closable
                  type="primary"
                  size="medium"
                  class="modern-tag-chip"
                  @close="removeKeywordFromConfig(index)"
                >
                  {{ keyword }}
                </el-tag>
              </div>

              <div class="action-row">
                <el-button type="primary" class="gradient-btn-small blue" icon="el-icon-plus" @click="showAddKeywordDialog">
                  Thêm từ khóa mới
                </el-button>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 2: Logo Management -->
        <el-tab-pane name="logo">
          <template slot="label">
            <i class="el-icon-picture-outline" /> Quản lý Logo hóa đơn
          </template>
          <div class="tab-inner-content">
            <!-- Rules description -->
            <div class="hint-panel orange-hint mb-4">
              <div class="hint-title">
                <i class="el-icon-warning"></i> Quy định tải lên Logo Merchant
              </div>
              <ul class="hint-list">
                <li>Logo sẽ được hiển thị trên thanh tiêu đề của liên kết thanh toán hosted-link WebView.</li>
                <li>Ảnh sau khi tải lên được lưu trữ trên S3 cloud và áp dụng tức thì trên mọi liên kết của hệ thống.</li>
                <li>Định dạng tệp tin cho phép: <strong>Chỉ JPG hoặc PNG</strong> (không hỗ trợ SVG, GIF, WebP).</li>
                <li>Dung lượng tệp tối đa: <strong>1MB</strong> (1,048,576 bytes). Tệp vượt quá dung lượng sẽ bị API từ chối.</li>
              </ul>
              <div class="warning-box mt-2">
                <span class="warn-label">⚠️ Lưu ý:</span>
                <span class="warn-text">Tải lên logo mới sẽ ghi đè và xóa hoàn toàn logo cũ, không thể hoàn tác.</span>
              </div>
            </div>

            <div class="logo-config-grid">
              <!-- Left side: Current Logo -->
              <div class="logo-display-card">
                <h3 class="section-title">Logo hiện tại</h3>
                <div class="logo-frame">
                  <el-image
                    v-if="config.logo_url"
                    :src="config.logo_url"
                    fit="contain"
                    class="current-logo-img"
                  >
                    <div slot="error" class="image-error-slot">
                      <i class="el-icon-picture-outline"></i>
                      <span>Lỗi tải logo</span>
                    </div>
                  </el-image>
                  <div v-else class="no-logo-slot">
                    <i class="el-icon-picture"></i>
                    <span>Chưa cấu hình Logo</span>
                  </div>
                </div>
              </div>

              <!-- Right side: Upload Controls -->
              <div class="logo-upload-controls">
                <h3 class="section-title">Upload Logo mới</h3>
                
                <el-upload
                  ref="logoUpload"
                  action="#"
                  :auto-upload="false"
                  :on-change="handleLogoChange"
                  :on-remove="handleLogoRemove"
                  :limit="1"
                  accept=".jpg,.jpeg,.png"
                  list-type="picture"
                  class="modern-logo-uploader mb-3"
                >
                  <el-button size="small" type="primary" icon="el-icon-folder-opened">
                    Chọn tệp logo mới
                  </el-button>
                  <div slot="tip" class="el-upload__tip">
                    Chỉ hỗ trợ tệp JPG/PNG, kích thước tối đa 1MB.
                  </div>
                </el-upload>

                <!-- Preview file upload -->
                <div v-if="logoFile" class="file-preview-card mb-3">
                  <h4 class="preview-title">Xem trước tệp chọn</h4>
                  <div class="preview-inner">
                    <el-image
                      v-if="logoPreview"
                      :src="logoPreview"
                      fit="contain"
                      class="preview-img-frame"
                    />
                    <div class="preview-info">
                      <p class="info-row"><strong>Tên file:</strong> {{ logoFile.name }}</p>
                      <p class="info-row"><strong>Dung lượng:</strong> {{ formatFileSize(logoFile.size) }}</p>
                      <p class="info-row"><strong>Định dạng:</strong> {{ logoFile.type }}</p>
                    </div>
                  </div>
                </div>

                <div class="action-row pt-2">
                  <el-button
                    type="success"
                    class="gradient-btn-small green"
                    icon="el-icon-upload"
                    :loading="isUploadingLogo"
                    :disabled="!logoFile"
                    @click="uploadLogoFile"
                  >
                    Bắt đầu tải lên
                  </el-button>
                  <el-button v-if="logoFile" class="cancel-upload-btn" @click="resetLogoUpload">
                    Hủy bỏ
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- Tab 3: Thông tin SePay -->
        <el-tab-pane name="sepay-token" lazy>
          <template slot="label">
            <i class="el-icon-key" /> Thông tin SePay
          </template>
          <se-pay-token-detail />
        </el-tab-pane>

        <!-- Tab 4: IP được chấp nhận -->
        <el-tab-pane name="sepay-ips" lazy>
          <template slot="label">
            <i class="el-icon-connection" /> IP được chấp nhận
          </template>
          <se-pay-allowed-ip-management />
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- Dialog: Add Keyword -->
    <el-dialog
      title="THÊM TỪ KHÓA GIAO DỊCH"
      :visible.sync="dialogAddKeyword"
      width="550px"
      custom-class="modern-dialog"
      @close="resetAddKeywordForm"
    >
      <el-form ref="formAddKeyword" :model="formKeyword" label-position="top">
        <el-form-item label="Từ khóa Whitelist (ví dụ: 'hoc phi', 'thanh toan', 'chuyen khoan')" prop="keyword">
          <el-input
            v-model="formKeyword.keyword"
            placeholder="Nhập từ khóa giao dịch..."
            prefix-icon="el-icon-edit"
            class="modern-input-large"
            @keyup.enter.native="addKeyword"
          />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogAddKeyword = false">Hủy bỏ</el-button>
        <el-button type="primary" class="gradient-btn-small blue" :loading="isSavingKeyword" @click="addKeyword">
          Xác nhận thêm
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MerchantConfigService from "@/services/MerchantConfigService";
import SePayTokenDetail from "./components/SePayTokenDetail.vue";
import SePayAllowedIpManagement from "./components/SePayAllowedIpManagement.vue";

export default {
  name: "MerchantManagement",
  components: { SePayTokenDetail, SePayAllowedIpManagement },
  data() {
    return {
      config: {
        transaction_whitelist_remark_keywords: [],
        logo_url: ""
      },
      formKeyword: {
        keyword: "",
        note: ""
      },
      logoFile: null,
      logoPreview: "",
      isInitialLoading: false,
      isSavingKeyword: false,
      isUploadingLogo: false,
      dialogAddKeyword: false,
      activeTab: "keywords",
      keywordsBackup: []
    };
  },

  mounted() {
    this.fetchMerchantConfig();
  },

  methods: {
    fetchMerchantConfig() {
      this.isInitialLoading = true;
      MerchantConfigService.getMerchantConfig()
        .then((response) => {
          if (response.data && response.data.data) {
            this.config = {
              transaction_whitelist_remark_keywords: response.data.data.transaction_whitelist_remark_keywords || [],
              logo_url: response.data.data.logo_url || ""
            };
            this.keywordsBackup = JSON.parse(JSON.stringify(this.config.transaction_whitelist_remark_keywords));
          }
          this.isInitialLoading = false;
        })
        .catch((error) => {
          this.isInitialLoading = false;
          console.error("Lỗi khi lấy cấu hình merchant:", error);
          this.$message.error("Không thể lấy cấu hình merchant. Vui lòng thử lại.");
        });
    },

    showAddKeywordDialog() {
      this.dialogAddKeyword = true;
      this.$nextTick(() => {
        this.$refs.formAddKeyword && this.$refs.formAddKeyword.clearValidate();
      });
    },

    addKeyword() {
      const keyword = this.formKeyword.keyword.trim();

      if (!keyword) {
        this.$message.warning("Vui lòng nhập từ khóa");
        return;
      }

      if (this.config.transaction_whitelist_remark_keywords.includes(keyword)) {
        this.$message.warning("Từ khóa này đã tồn tại");
        return;
      }

      if (keyword.length > 100) {
        this.$message.warning("Từ khóa không được quá 100 ký tự");
        return;
      }

      this.config.transaction_whitelist_remark_keywords.push(keyword);
      this.isSavingKeyword = true;

      MerchantConfigService.updateMerchantConfig({
        transaction_whitelist_remark_keywords: this.config.transaction_whitelist_remark_keywords
      })
        .then(() => {
          this.$message.success("Thêm từ khóa thành công");
          this.keywordsBackup = JSON.parse(JSON.stringify(this.config.transaction_whitelist_remark_keywords));
          this.resetAddKeywordForm();
          this.dialogAddKeyword = false;
          this.isSavingKeyword = false;
        })
        .catch((error) => {
          this.isSavingKeyword = false;
          console.error("Lỗi khi thêm từ khóa:", error);
          this.$message.error("Thêm từ khóa thất bại. Vui lòng thử lại.");
          this.config.transaction_whitelist_remark_keywords.pop();
        });
    },

    removeKeywordFromConfig(index) {
      this.$confirm("Bạn có chắc muốn xóa từ khóa này?", "Xác nhận xóa", {
        confirmButtonText: "Xóa",
        cancelButtonText: "Hủy",
        type: "warning"
      })
        .then(() => {
          const keyword = this.config.transaction_whitelist_remark_keywords[index];
          this.config.transaction_whitelist_remark_keywords.splice(index, 1);
          
          MerchantConfigService.updateMerchantConfig({
            transaction_whitelist_remark_keywords: this.config.transaction_whitelist_remark_keywords
          })
            .then(() => {
              this.$message.success("Xóa từ khóa thành công");
              this.keywordsBackup = JSON.parse(JSON.stringify(this.config.transaction_whitelist_remark_keywords));
            })
            .catch((error) => {
              console.error("Lỗi khi xóa từ khóa:", error);
              this.$message.error("Xóa từ khóa thất bại. Vui lòng thử lại.");
              this.config.transaction_whitelist_remark_keywords.splice(index, 0, keyword);
            });
        })
        .catch(() => {});
    },

    resetAddKeywordForm() {
      this.formKeyword = {
        keyword: "",
        note: ""
      };
    },

    handleLogoChange(file) {
      this.logoFile = file.raw;

      if (!this.validateLogoFile(this.logoFile)) {
        this.logoFile = null;
        this.logoPreview = "";
        this.$refs.logoUpload && this.$refs.logoUpload.clearFiles();
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(this.logoFile);
    },

    handleLogoRemove() {
      this.logoFile = null;
      this.logoPreview = "";
    },

    validateLogoFile(file) {
      const validTypes = ["image/jpeg", "image/png"];
      if (!validTypes.includes(file.type)) {
        this.$message.error("Chỉ hỗ trợ file JPG/PNG");
        return false;
      }

      const maxSize = 1024 * 1024;
      if (file.size > maxSize) {
        this.$message.error("File không được vượt quá 1MB");
        return false;
      }

      return true;
    },

    uploadLogoFile() {
      if (!this.logoFile) {
        this.$message.warning("Vui lòng chọn file logo");
        return;
      }

      this.$confirm(
        "Logo cũ sẽ tự động bị xóa và không thể rollback. Bạn có chắc muốn upload logo này?",
        "Xác nhận upload",
        {
          confirmButtonText: "Tải lên",
          cancelButtonText: "Hủy bỏ",
          type: "warning"
        }
      )
        .then(() => {
          this.isUploadingLogo = true;

          MerchantConfigService.uploadLogo(this.logoFile)
            .then((response) => {
              if (response.data && response.data.data) {
                this.config.logo_url = response.data.data;
                this.$message.success("Upload logo thành công");
                this.resetLogoUpload();
                this.fetchMerchantConfig();
              }
              this.isUploadingLogo = false;
            })
            .catch((error) => {
              this.isUploadingLogo = false;
              console.error("Lỗi khi upload logo:", error);

              if (error.response && error.response.data && error.response.data.message) {
                this.$message.error(error.response.data.message);
              } else {
                this.$message.error("Upload logo thất bại. Vui lòng kiểm tra lại kích thước hoặc định dạng file.");
              }
            });
        })
        .catch(() => {});
    },

    resetLogoUpload() {
      this.logoFile = null;
      this.logoPreview = "";
      this.$refs.logoUpload && this.$refs.logoUpload.clearFiles();
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-check-container {
  padding: 16px;
  background-color: #f4f6fa;
  min-height: calc(100vh - 120px);
}

/* Header Banner */
.ai-header-banner {
  position: relative;
  border-radius: 12px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 24px 32px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(30, 60, 114, 0.15);

  .banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  }

  .banner-content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
    width: 100%;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .ai-icon-pulse {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
    animation: iconPulse 3s infinite ease-in-out;

    i {
      font-size: 32px;
      color: #fff;
    }
  }

  .banner-title {
    color: #fff;
    font-size: 22px;
    margin: 0 0 6px 0;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .banner-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    margin: 0;
  }
}

@keyframes iconPulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 15px rgba(255, 255, 255, 0.2); }
  50% { transform: scale(1.08); box-shadow: 0 0 25px rgba(255, 255, 255, 0.4); }
}

.loading-container {
  min-height: 300px;
}

/* Glassmorphic Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.panel-container {
  background: #fff;
}

/* Tabs customization - chuẩn guidelines.md mục 5 */
.sk-tabs {
  /deep/ .el-tabs__header {
    border-bottom: 2px solid #2563eb;
    margin-bottom: 20px;
  }
  
  /deep/ .el-tabs__nav {
    border: none !important;
    background: transparent !important;
  }

  /deep/ .el-tabs__item {
    background: #e5e7eb;
    color: #4b5563 !important;
    font-weight: 600;
    border: 1px solid #d1d5db !important;
    border-bottom: none !important;
    border-radius: 8px 8px 0 0;
    transition: background 0.15s, color 0.15s;
    margin-right: 4px;

    i {
      margin-right: 6px;
    }

    &:hover:not(.is-active) {
      background: #d1d5db;
      color: #1e293b !important;
    }
    
    &.is-active {
      color: #ffffff !important;
      background: #2563eb !important;
      border-color: #2563eb !important;
    }
  }
}

.tab-inner-content {
  padding: 8px 0;
}

/* Hint panel boxes */
.hint-panel {
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;

  .hint-title {
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .hint-list {
    margin: 0;
    padding-left: 20px;
    color: #475569;
  }

  &.blue-hint {
    background-color: #f0f9ff;
    border-left: 4px solid #3b82f6;
    .hint-title { color: #1d4ed8; }
    
    .example-box {
      border-top: 1px solid #bae6fd;
      padding-top: 8px;
      font-size: 12px;
      .ex-label { font-weight: 700; color: #0369a1; }
      .ex-text { color: #0284c7; }
    }
  }

  &.orange-hint {
    background-color: #fffbeb;
    border-left: 4px solid #f59e0b;
    .hint-title { color: #b45309; }

    .warning-box {
      border-top: 1px solid #fef3c7;
      padding-top: 8px;
      font-size: 12px;
      .warn-label { font-weight: 700; color: #b45309; }
      .warn-text { color: #d97706; }
    }
  }
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
  letter-spacing: 0.3px;
}

/* Empty State Card */
.empty-state-card {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  background-color: #f8fafc;
  
  i {
    font-size: 36px;
    color: #94a3b8;
    margin-bottom: 10px;
  }

  .empty-text {
    margin: 0;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
}

/* Keywords tag chips grid */
.keywords-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.modern-tag-chip {
  font-weight: 600;
  font-size: 13px;
  height: 32px;
  line-height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  transition: all 0.3s;
  
  /deep/ .el-tag__close {
    color: #3b82f6;
    &:hover {
      background-color: #2563eb;
      color: #fff;
    }
  }
}

.gradient-btn-small {
  border: none;
  font-weight: 600;
  padding: 9px 18px;
  border-radius: 6px;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-1px);
  }

  &.blue {
    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
    box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
  }

  &.green {
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
    box-shadow: 0 4px 10px rgba(16, 185, 129, 0.3);
  }
}

/* Logo config grid layout */
.logo-config-grid {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 32px;
  align-items: flex-start;
}

.logo-display-card {
  .logo-frame {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 16px;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
  }

  .current-logo-img {
    max-width: 100%;
    max-height: 100%;
  }

  .image-error-slot, .no-logo-slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;

    i { font-size: 40px; opacity: 0.7; }
  }
}

.logo-upload-controls {
  .modern-logo-uploader {
    /deep/ .el-upload-list--picture .el-upload-list__item {
      border-radius: 8px;
      border: 1px solid #cbd5e1;
    }
  }

  .cancel-upload-btn {
    border-radius: 6px;
    padding: 9px 18px;
    margin-left: 8px;
  }
}

/* File upload preview */
.file-preview-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;

  .preview-title {
    margin: 0 0 12px 0;
    font-size: 13px;
    font-weight: 700;
    color: #475569;
  }

  .preview-inner {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .preview-img-frame {
    width: 120px;
    height: 80px;
    border: 1px solid #cbd5e1;
    background: #fff;
    border-radius: 6px;
    padding: 4px;
  }

  .preview-info {
    flex: 1;
    .info-row {
      margin: 4px 0;
      font-size: 12px;
      color: #64748b;
      strong { color: #334155; }
    }
  }
}

/* Dialog additions */
.modern-dialog {
  border-radius: 12px !important;
  
  .modern-input-large {
    /deep/ .el-input__inner {
      height: 44px;
      font-size: 14px;
      border-radius: 8px;
    }
  }
}

.mb-3 { margin-bottom: 12px; }
.mb-4 { margin-bottom: 16px; }
.mt-2 { margin-top: 8px; }
.pt-2 { padding-top: 8px; }
.flex { display: flex; }
.flex-column { flex-direction: column; }
.justify-center { justify-content: center; }
.align-center { align-items: center; }
</style>
